// eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-imports-ts
import { Component, Host, Element, Prop, State, Watch, h, Fragment, Env } from '@stencil/core';

import { DeviceType } from '../../../../types/base/affiliate/characteristics';
import { AffiliateControllerResponse } from '../../../../types/controllers/affiliate';
import { FormattedPromotion } from '../../types';

import { formatPromotions } from '../../utils';
import { ageClass } from '../../utils/age-check';
import { getPlacement } from '../../utils/api';
import { getPromotionTableTitle } from '../../utils/location';

import state, { setLocationToStore, stencilStore } from '../../utils/store';
import FeaturedPromotionCard from '../functional/FeaturedPromotionCard';
import LocationDropdown from '../functional/LocationDropdown';

import PromotionCard from '../functional/PromotionCard';

import type { PromotionOptions, ComponentType } from '../../utils/analytics';

@Component({
    tag: 'bam-promotion-list',
    styleUrl: 'bam-promotion-list.css',
    shadow: true,
})
export class BamPromotionList {
    private defaultCallback() {
        return null;
    }

    @Element() el: HTMLElement;

    // props
    /** (required) */
    @Prop() placementId: number;
    /** (required) */
    @Prop() propertyId: number;
    @Prop({ mutable: true }) location: string;
    /** (required) */
    @Prop() context: string;
    /** You cannot pass this parameter via an attribute. You must set the callback function after the DOM has loaded. */
    @Prop() callback: () => void = this.defaultCallback;

    // optional characteristics props
    @Prop() pageSlug: string;
    @Prop() subscriptionTier: 0 | 1 | 2 | 3;
    @Prop() wasPro: boolean;
    @Prop() subdivision: number;
    @Prop() league: string;
    @Prop() gameId: number;
    @Prop() ctaType: string;
    @Prop() isMobile: boolean;
    @Prop() deviceType: DeviceType;
    @Prop() isBetsyncUser: boolean;

    // optional props for functionality
    @Prop() showFeaturedPromotion = false;
    /** If provided list will render a “See all”/”Hide” button, capping the number of promotions shown to the max length in its collapsed state */
    @Prop() maxLength: number;
    /** Changes the title shown in the header of the component. Defaults to “Offers”. */
    @Prop() moduleTitle: string;
    @Prop() defer: boolean;
    @Prop() devMode = false;
    @Prop() age: number;
    @Prop() hideUnderage = false;
    @Prop() darkMode = false;
    @Prop() terms: 'geo' | 'affiliate' = 'geo';

    // state
    @State() promotions: FormattedPromotion[] = [];
    @State() promotionListId: number;
    @State() toggle = false;
    @State() loading = true;
    @State() locationToUse: string | null = null;

    @Watch('defer')
    onDeferChange(newDeferValue) {
        if (!newDeferValue) {
            getPlacement(
                {
                    placement_id: this.placementId,
                    property_id: this.propertyId,
                    location: this.locationToUse,
                    page_slug: this.pageSlug,
                    subscription_tier: this.subscriptionTier,
                    was_pro: this.wasPro,
                    subdivision_id: this.subdivision,
                    league: this.league,
                    game_id: this.gameId,
                    cta_type: this.ctaType,
                    is_mobile: this.isMobile,
                    device_type: this.deviceType,
                    isBetsyncUser: this.isBetsyncUser,
                    context: this.context,
                },
                this.formatAndSetPromotionsToState.bind(this),
                this.devMode,
                'Promotion List'
            );
        }
    }

    @Watch('locationToUse')
    @Watch('subscriptionTier')
    onLocationChange() {
        if (!this.defer) {
            getPlacement(
                {
                    placement_id: this.placementId,
                    property_id: this.propertyId,
                    location: this.locationToUse,
                    page_slug: this.pageSlug,
                    subscription_tier: this.subscriptionTier,
                    was_pro: this.wasPro,
                    subdivision_id: this.subdivision,
                    league: this.league,
                    game_id: this.gameId,
                    cta_type: this.ctaType,
                    is_mobile: this.isMobile,
                    device_type: this.deviceType,
                    isBetsyncUser: this.isBetsyncUser,
                    context: this.context,
                },
                this.formatAndSetPromotionsToState.bind(this),
                this.devMode,
                'Promotion List'
            );
        }
    }

    @Watch('location')
    onLocationPropChange() {
        this.locationToUse = this.location || state.location;
    }

    connectedCallback() {
        this.locationToUse = this.location || state.location;

        if (!this.defer && Env.BAM_HYDRATE !== 'true') {
            getPlacement(
                {
                    placement_id: this.placementId,
                    property_id: this.propertyId,
                    location: this.locationToUse,
                    page_slug: this.pageSlug,
                    subscription_tier: this.subscriptionTier,
                    was_pro: this.wasPro,
                    subdivision_id: this.subdivision,
                    league: this.league,
                    game_id: this.gameId,
                    cta_type: this.ctaType,
                    is_mobile: this.isMobile,
                    device_type: this.deviceType,
                    isBetsyncUser: this.isBetsyncUser,
                    context: this.context,
                },
                this.formatAndSetPromotionsToState.bind(this),
                this.devMode,
                'Promotion List'
            );
        }

        // if location is not provided, subscribe to location in store
        stencilStore.onChange('location', newLocation => {
            this.locationToUse = newLocation;
        });
    }

    async componentWillLoad() {
        if (Env.BAM_HYDRATE === 'true') {
            await getPlacement(
                {
                    placement_id: this.placementId,
                    property_id: this.propertyId,
                    location: this.locationToUse,
                    page_slug: this.pageSlug,
                    subscription_tier: this.subscriptionTier,
                    was_pro: this.wasPro,
                    subdivision_id: this.subdivision,
                    league: this.league,
                    game_id: this.gameId,
                    cta_type: this.ctaType,
                    is_mobile: this.isMobile,
                    device_type: this.deviceType,
                    isBetsyncUser: this.isBetsyncUser,
                    context: this.context,
                },
                this.formatAndSetPromotionsToState.bind(this),
                this.devMode,
                'Promotion List'
            );
        }
    }

    handleDropdown(evt: Event) {
        if (evt.target instanceof HTMLSelectElement) {
            const newLocation = evt.target.value;
            setLocationToStore(newLocation);
        }
    }

    handleToggle() {
        this.toggle = !this.toggle;
    }

    formatAndSetPromotionsToState(placement: AffiliateControllerResponse, location: string) {
        const promotions = formatPromotions(placement.promotions, {
            allowAltLink: placement.allow_alternate_link,
            promotionListId: placement.id,
            location: location,
            context: this.context,
            propertyId: this.propertyId,
        });

        if (!promotions.length) {
            // eslint-disable-next-line no-console
            console.warn('BAM Promotion List: No active or valid promotions returned');
            this.promotions = promotions;
            return;
        }

        this.promotions = promotions;
        this.locationToUse = !this.location ? location : this.locationToUse;
        this.promotionListId = placement.id;
        this.loading = false;
    }

    render() {
        return (
            <Host class={ageClass(this.age, this.promotions, this.hideUnderage)}>
                <div class={`${this.darkMode ? 'dark-mode' : ''}`}>
                    <div class="side-rail font">
                        <div class="header">
                            <h2 class="title title-font">
                                {getPromotionTableTitle(this.locationToUse, this.moduleTitle)}
                            </h2>
                            <LocationDropdown
                                location={this.locationToUse}
                                handleDropdown={this.handleDropdown.bind(this)}
                            />
                        </div>
                        <ul id="promo-container" class="list-container">
                            {!this.defer && !this.loading ? (
                                <Fragment>
                                    {this.promotions.length ? (
                                        <Fragment>
                                            {this.promotions
                                                .slice(0, this.toggle ? this.promotions.length : this.maxLength)
                                                .map((promotion, idx) => {
                                                    const eventOptions: {
                                                        componentType: ComponentType;
                                                        options: PromotionOptions;
                                                    } = {
                                                        componentType: 'Promotion List',
                                                        options: {
                                                            stateCode: this.locationToUse?.toUpperCase(),
                                                            context: this.context,
                                                            propertyId: Number(this.propertyId),
                                                            placementId: Number(this.placementId),
                                                            league: this.league,
                                                            allPromotions: this.promotions,
                                                            pageSlug: this.pageSlug,
                                                        },
                                                    };

                                                    return (
                                                        <promotion-card
                                                            key={promotion.id}
                                                            isFeatured={this.showFeaturedPromotion && idx === 0}
                                                            promotion={promotion}
                                                            propertyId={this.propertyId}
                                                            eventOptions={eventOptions}
                                                            dark-mode={this.darkMode}
                                                            terms={this.terms}
                                                            callback={this.callback}
                                                        ></promotion-card>
                                                    );
                                                })}
                                        </Fragment>
                                    ) : (
                                        <p class="empty-state">
                                            No promotions available. Try selecting a different location.
                                        </p>
                                    )}
                                </Fragment>
                            ) : (
                                <Fragment>
                                    {this.showFeaturedPromotion ? (
                                        <FeaturedPromotionCard
                                            promotion={null}
                                            logPromotionClick={() => {}}
                                            handleBonusCodeClick={() => {}}
                                            bonusCodeWasClicked={false}
                                            terms={this.terms}
                                        />
                                    ) : (
                                        <PromotionCard
                                            promotion={null}
                                            logPromotionClick={() => {}}
                                            handleBonusCodeClick={() => {}}
                                            bonusCodeWasClicked={false}
                                            terms={this.terms}
                                        ></PromotionCard>
                                    )}
                                </Fragment>
                            )}
                        </ul>
                        {this.promotions.length > this.maxLength ? (
                            <div class="footer">
                                <button class="see-all" onClick={this.handleToggle.bind(this)}>
                                    {this.toggle ? 'Hide' : 'See all'}
                                </button>
                            </div>
                        ) : null}
                    </div>
                </div>
            </Host>
        );
    }
}
